import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@halo/web-lib';
import { useIngestionSSE } from '../../hooks/useIngestionSSE';
import {
  Clock,
  ChevronLeft,
  Rows3,
  ScrollText,
  AlertCircle,
  CircleDot,
  CheckCircle2,
  XCircle,
  Layers,
  Zap,
  Link2,
  Copy,
  KeyRound,
  ShieldAlert,
} from 'lucide-react';
import type { IngestionRun } from './types';
import { STATUS_CONFIG, RUN_TYPE_LABELS, TRIGGER_SOURCE_LABELS, formatDuration } from './types';
import { StatCard } from './components';

export function IngestionRunDetail() {
  const { storeId, configId, runId } = useParams<{ storeId: string; configId: string; runId: string }>();

  const [run, setRun] = useState<IngestionRun | null>(null);
  const [loading, setLoading] = useState(true);
  const logsEndRef = useRef<HTMLDivElement>(null);

  const fetchRun = useCallback(async () => {
    const res = await api.get(`/api/v1/ingestion/admin/runs/${runId}`);
    if (res.success && res.data) setRun(res.data as IngestionRun);
    setLoading(false);
  }, [runId]);

  useEffect(() => {
    fetchRun();
    // Slow fallback poll (60s) — SSE handles real-time updates
    const interval = setInterval(fetchRun, 60_000);
    return () => clearInterval(interval);
  }, [fetchRun]);

  // SSE real-time updates
  useIngestionSSE({
    storeId: run?.storeId,
    platform: run?.platform,
    enabled: !!run,
    onEvent: useCallback(() => {
      fetchRun();
    }, [fetchRun]),
  });

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [run?.logs?.length]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48" />
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  if (!run) {
    return (
      <div>
        <Link to={`/ingestion/${storeId}/${configId}`} className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4">
          <ChevronLeft className="w-4 h-4" /> Back
        </Link>
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">Run not found</p>
        </div>
      </div>
    );
  }

  const rs = STATUS_CONFIG[run.status] ?? STATUS_CONFIG['completed']!;
  const RunStatusIcon = rs.icon;
  const computedDurationMs =
    run.durationMs != null
      ? run.durationMs
      : run.startedAt && run.completedAt
        ? new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime()
        : run.startedAt
          ? Date.now() - new Date(run.startedAt).getTime()
          : null;
  const isRunning = run.status === 'running' || !run.completedAt;
  const duration =
    computedDurationMs != null
      ? formatDuration(computedDurationMs) + (isRunning ? ' (running)' : '')
      : null;

  const circuitOpenPlatforms: string[] = (() => {
    if (run.status !== 'partial' || !run.logs?.length) return [];
    const set = new Set<string>();
    for (const entry of run.logs) {
      const m = /circuit_open:(.+)/.exec(entry.msg);
      if (m && m[1]) set.add(m[1].trim());
    }
    return Array.from(set);
  })();

  const rowsWrittenEntries: Array<[string, number]> =
    run.rowsWritten && typeof run.rowsWritten === 'object'
      ? Object.entries(run.rowsWritten).filter(([, v]) => typeof v === 'number')
      : [];
  const hasRowsWritten = rowsWrittenEntries.length > 0;
  const rowsWrittenTotal = rowsWrittenEntries.reduce((acc, [, v]) => acc + v, 0);

  const copyIdempotencyKey = () => {
    if (run.idempotencyKey && typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(run.idempotencyKey);
    }
  };

  return (
    <div>
      <Link to={`/ingestion/${storeId}/${configId}`} className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-3">
        <ChevronLeft className="w-4 h-4" /> Back to config
      </Link>

      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {RUN_TYPE_LABELS[run.runType] ?? run.runType} Run
            {(() => {
              const ts = TRIGGER_SOURCE_LABELS[run.triggerSource];
              return ts ? (
                <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium align-middle ${ts.className}`}>{ts.label}</span>
              ) : null;
            })()}
          </h2>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{run.id}</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${rs.className}`}>
          <RunStatusIcon className="w-3.5 h-3.5" />
          {rs.label}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <StatCard label="Rows" value={run.rowsIngested.toLocaleString()} icon={Rows3} />
        <StatCard label="Duration" value={duration ?? '—'} icon={Clock} />
        <StatCard label="Window" value={run.windowStart ? (run.windowStart === run.windowEnd ? run.windowStart : `${run.windowStart} → ${run.windowEnd}`) : '—'} icon={CircleDot} />
        <StatCard
          label="Throughput"
          value={(() => {
            const ms = computedDurationMs ?? 0;
            const rowsPerSec = ms > 0 ? (run.rowsIngested / (ms / 1000)).toFixed(1) : '0';
            return `${rowsPerSec} rows/s`;
          })()}
          icon={Zap}
        />
      </div>

      {/* Run identity — idempotency key + raw duration */}
      {(run.idempotencyKey || run.durationMs != null) && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-3 mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
          {run.idempotencyKey && (
            <div className="flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
              <span className="text-gray-500 dark:text-gray-400">Idempotency key</span>
              <code
                className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono text-[10px] text-gray-700 dark:text-gray-200"
                title={run.idempotencyKey}
              >
                {run.idempotencyKey.slice(0, 12)}…
              </code>
              <button
                type="button"
                onClick={copyIdempotencyKey}
                className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                title="Copy full hash"
              >
                <Copy className="w-3 h-3" /> copy
              </button>
            </div>
          )}
          {run.durationMs != null && (
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
              <span className="text-gray-500 dark:text-gray-400">Run duration</span>
              <span className="font-mono text-[10px] text-gray-700 dark:text-gray-200">
                {formatDuration(run.durationMs)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Partial run + circuit-open banner */}
      {run.status === 'partial' && circuitOpenPlatforms.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-xl p-3 mb-4">
          <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
            <ShieldAlert className="w-3.5 h-3.5" /> Circuit breaker was open
          </div>
          <p className="text-xs text-amber-800 dark:text-amber-200">
            Circuit breaker was open for{' '}
            <span className="font-medium">{circuitOpenPlatforms.join(', ')}</span>{' '}
            during this run.{' '}
            <Link
              to="/admin/circuit-breakers"
              className="underline hover:text-amber-900 dark:hover:text-amber-100"
            >
              Check the breakers page
            </Link>
            .
          </p>
        </div>
      )}

      {/* Rows written breakdown (per-table) */}
      {hasRowsWritten && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden mb-4">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
            <Rows3 className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Rows written</span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto">
              {rowsWrittenEntries.length} table{rowsWrittenEntries.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {rowsWrittenEntries.map(([table, count]) => (
              <div key={table} className="flex items-center justify-between px-4 py-1.5 text-xs">
                <code className="font-mono text-gray-700 dark:text-gray-200">{table}</code>
                <span className="font-mono text-gray-600 dark:text-gray-300">
                  {count.toLocaleString()}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between px-4 py-1.5 text-xs bg-gray-50 dark:bg-gray-950 font-medium">
              <span className="text-gray-700 dark:text-gray-200">Total</span>
              <span className="font-mono text-gray-800 dark:text-gray-100">
                {rowsWrittenTotal.toLocaleString()}
                {rowsWrittenTotal !== run.rowsIngested && (
                  <span className="ml-2 text-[10px] text-gray-400 dark:text-gray-500" title="rowsIngested differs from sum of rowsWritten">
                    (rowsIngested: {run.rowsIngested.toLocaleString()})
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {run.backfillGroupId && (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3 bg-gray-50 dark:bg-gray-950 rounded-lg px-3 py-2 border border-gray-100 dark:border-gray-800">
          <Link2 className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          <span>Part of backfill group</span>
          <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono text-gray-600 dark:text-gray-300">
            {run.backfillGroupId.slice(0, 24)}…
          </code>
        </div>
      )}

      {run.errorMessage && (
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-xl p-3 mb-4">
          <div className="flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400 mb-1">
            <AlertCircle className="w-3.5 h-3.5" /> Error
          </div>
          <p className="text-xs text-red-700 dark:text-red-400 whitespace-pre-wrap break-words">{run.errorMessage}</p>
        </div>
      )}

      {/* Window Checkpoints — per-window progress with verification */}
      {run.windowCheckpoints && run.windowCheckpoints.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden mb-4">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
            <Layers className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Window Checkpoints</span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto">
              {run.windowCheckpoints.filter(c => c.status === 'completed').length}/{run.windowCheckpoints.length} completed
              {run.windowsTotal && run.windowsTotal > run.windowCheckpoints.length
                ? ` (${run.windowsTotal} total)` : ''}
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {run.windowCheckpoints.map((cp, i) => {
              const isVerified = cp.rowsInClickHouse !== null && cp.rowsInClickHouse !== undefined;
              const verifyMismatch = isVerified && cp.rowsFromCollector > 0 && cp.rowsInClickHouse === 0;
              const windowDuration = cp.durationMs != null ? formatDuration(cp.durationMs) : null;
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-2 text-xs">
                  <div className="flex-shrink-0">
                    {cp.status === 'completed' ? (
                      <CheckCircle2 className={`w-4 h-4 ${verifyMismatch ? 'text-amber-500' : 'text-green-500'}`} />
                    ) : cp.status === 'errored' ? (
                      <XCircle className="w-4 h-4 text-red-500" />
                    ) : (
                      <CircleDot className="w-4 h-4 text-gray-300" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="font-mono text-gray-700 dark:text-gray-200">
                      {cp.start === cp.end ? cp.start : `${cp.start} → ${cp.end}`}
                    </span>
                  </div>
                  <div className="text-right text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <span>{cp.rowsFromCollector.toLocaleString()} rows</span>
                    {isVerified && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        verifyMismatch
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        CH: {cp.rowsInClickHouse!.toLocaleString()}
                      </span>
                    )}
                    {windowDuration && (
                      <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-[10px] font-medium">
                        {windowDuration}
                      </span>
                    )}
                    {cp.attempt > 1 && (
                      <span className="px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 text-[10px] font-medium">
                        attempt {cp.attempt}
                      </span>
                    )}
                  </div>
                  {cp.status === 'errored' && cp.errorMessage && (
                    <div className="text-red-500 truncate max-w-[200px]" title={cp.errorMessage}>
                      {cp.errorMessage}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Window Progress Bar (for running backfills) */}
      {run.status === 'running' && run.windowsTotal && run.windowsTotal > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-3 mb-4">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Window Progress</span>
            <span>{run.windowsCompleted ?? 0} / {run.windowsTotal}</span>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${Math.round(((run.windowsCompleted ?? 0) / run.windowsTotal) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Logs */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
          <ScrollText className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Logs</span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto">{run.logs.length} entries</span>
        </div>
        <div className="max-h-96 overflow-auto font-mono text-xs p-3 bg-gray-950 text-gray-300">
          {run.logs.length === 0 ? (
            <div className="text-gray-600 dark:text-gray-300 text-center py-8">No log entries yet</div>
          ) : (
            run.logs.map((entry, i) => (
              <div key={i} className="flex gap-2 py-0.5 hover:bg-gray-900/50">
                <span className="text-gray-600 dark:text-gray-300 whitespace-nowrap shrink-0">
                  {new Date(entry.ts).toLocaleTimeString()}
                </span>
                <span
                  className={
                    entry.level === 'error'
                      ? 'text-red-400'
                      : entry.level === 'warn'
                        ? 'text-amber-400'
                        : 'text-gray-400 dark:text-gray-500'
                  }
                >
                  [{entry.level.toUpperCase()}]
                </span>
                <span
                  className={
                    entry.level === 'error'
                      ? 'text-red-300'
                      : entry.level === 'warn'
                        ? 'text-amber-200'
                        : 'text-gray-300'
                  }
                >
                  {entry.msg}
                </span>
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      </div>
    </div>
  );
}
